import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Trophy, Target, Users, MessageSquare, Coins, Clock, Gamepad2, Play } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * CommunitySection — the full community experience rendered inline on a
 * streamer's home page. This is the ONLY community surface (there is no
 * separate `/c/:username/community` page anymore).
 *
 * Sections included:
 *   • Hero row w/ 4-stat glance (level + members + chats + tipped)
 *   • "Your identity here" (only when the viewer is a member)
 *   • Top Supporters + Community Challenges (grid)
 *   • Community Milestones (grid)
 *   • Members Leaderboard (sortable)
 */
export default function CommunitySection({ channel }) {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [me, setMe] = useState(null);
  const [sortBy, setSortBy] = useState("xp");

  useEffect(() => {
    if (!channel?.username) return;
    const load = async () => {
      try { const s = await api.get(`/channels/${channel.username}/community/stats`); setStats(s.data); } catch { /* */ }
      try {
        const c = user
          ? await api.get(`/channels/${channel.username}/challenges`)
          : await api.get(`/channels/${channel.username}/challenges/public`);
        setChallenges(c.data || []);
      } catch { /* */ }
      try {
        const lb = await api.get(`/channels/${channel.username}/community/leaderboard?sort=${sortBy}&limit=15`);
        setLeaderboard(lb.data || []);
      } catch { /* */ }
      if (user) {
        try { const m = await api.get(`/channels/${channel.username}/community/member/${user.username}`); setMe(m.data); } catch { /* */ }
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [channel?.username, user?.username, sortBy]);

  if (!stats) {
    return <div className="sc-card p-8 text-center text-xs text-zinc-500" data-testid="community-section-loading">Loading community…</div>;
  }

  const xpPct = Math.min(100, Math.round((stats.xp_into_level / (stats.next_level_xp || 1000)) * 100));

  return (
    <div className="space-y-6" data-testid="community-section">
      {/* Hero + 4-stat glance */}
      <div className="sc-card p-6 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full opacity-25 blur-3xl pointer-events-none" style={{ background: "radial-gradient(circle,#CCFF00,#FF5900,transparent)" }} />
        <div className="relative">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
            <div>
              <div className="sc-label" style={{ color: "var(--sc-volt)" }}>◤ COMMUNITY HUB</div>
              <div className="sc-heading text-3xl font-black mt-1">{channel.username}&apos;s world</div>
              <p className="text-sm text-zinc-400 mt-1 max-w-lg">{channel.description || "A living, breathing community around a streamer."}</p>
            </div>
            <Link to={`/c/${channel.username}/live`} className="sc-btn text-sm flex items-center gap-2" data-testid="community-visit-channel">
              <Play className="w-4 h-4" /> {channel.is_live ? "Watch Live" : "Visit Channel"}
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="p-4 border border-[color:var(--sc-volt)]/30 bg-[color:var(--sc-volt)]/5 rounded-sm" data-testid="community-stat-level">
              <div className="sc-label text-[color:var(--sc-volt)]">Community Level</div>
              <div className="sc-heading text-3xl font-black mt-1">{stats.community_level}</div>
              <div className="text-[10px] sc-mono text-zinc-500">{stats.total_xp.toLocaleString()} XP</div>
              <div className="h-1.5 mt-2 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full" style={{ width: `${xpPct}%`, background: "linear-gradient(90deg,#CCFF00,#FF5900,#FF1053)" }} />
              </div>
            </div>
            <StatBox icon={Users}         label="Members" value={stats.member_count.toLocaleString()}                tone="var(--sc-cyan)"   testid="community-stat-members" />
            <StatBox icon={MessageSquare} label="Chats"   value={(stats.totals?.chats ?? 0).toLocaleString()}         tone="var(--sc-pink)"   testid="community-stat-chats" />
            <StatBox icon={Coins}         label="Tipped"  value={(stats.totals?.tips ?? 0).toLocaleString()}          tone="var(--sc-orange)" testid="community-stat-tipped" />
          </div>
        </div>
      </div>

      {/* Your identity */}
      {user && me && !me.not_a_member && (
        <div className="sc-card p-5" data-testid="community-my-identity">
          <div className="flex items-center gap-2 mb-4">
            <Crown className="w-4 h-4 text-[color:var(--sc-orange)]" />
            <div className="sc-heading text-lg font-bold">Your identity here</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="p-4 border border-white/10 rounded-sm">
              <div className="sc-label">Your Level</div>
              <div className="sc-heading text-3xl font-black mt-1" style={{ color: "var(--sc-volt)" }}>{me.level}</div>
              <div className="text-[10px] sc-mono text-zinc-500">{me.xp} XP earned</div>
            </div>
            <StatBox icon={MessageSquare} label="Chats"     value={me.chat_count || 0}     tone="var(--sc-pink)" />
            <StatBox icon={Clock}         label="Watch min" value={me.watch_minutes || 0}  tone="var(--sc-cyan)" />
            <StatBox icon={Coins}         label="Tipped"    value={me.tips_total || 0}     tone="var(--sc-orange)" />
          </div>
          {(me.badges || []).length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="community-my-badges">
              {me.badges.map((b) => (
                <span key={b.key} className="text-[10px] sc-mono px-2 py-1 rounded-sm border" style={{ borderColor: `${b.color}80`, color: b.color }}>{b.label}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Top Supporters + Community Challenges */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="sc-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <Crown className="w-4 h-4 text-[color:var(--sc-orange)]" />
            <div className="sc-heading text-lg font-bold">Top Supporters</div>
          </div>
          {stats.top_supporters.length === 0 && <div className="text-xs text-zinc-500">Be the first tipper and claim Top Supporter forever.</div>}
          {stats.top_supporters.map((s, i) => (
            <div key={s.username} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-b-0" data-testid={`community-supporter-${s.username}`}>
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs"
                style={{ background: i === 0 ? "linear-gradient(135deg,#FF5900,#F59E0B)" : i === 1 ? "linear-gradient(135deg,#FF1053,#A78BFA)" : "linear-gradient(135deg,#00E5FF,#CCFF00)", color: "#0A0A0C" }}
              >{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{s.username}</div>
                <div className="text-[10px] sc-mono text-zinc-500">Lv.{s.level} · {s.xp} XP</div>
              </div>
              <div className="text-[color:var(--sc-orange)] sc-mono font-bold">{s.tips_total}</div>
            </div>
          ))}
        </div>

        <div className="sc-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-4 h-4 text-[color:var(--sc-pink)]" />
            <div className="sc-heading text-lg font-bold">Community Challenges</div>
          </div>
          {challenges.length === 0 && <div className="text-xs text-zinc-500">Streamer hasn&apos;t posted any challenges yet.</div>}
          {challenges.map((c) => {
            const my = c.my_progress || 0;
            const pct = Math.min(100, Math.round((my / c.target) * 100));
            return (
              <div key={c.id} className="py-2 border-b border-white/5 last:border-b-0" data-testid={`community-challenge-${c.id}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-semibold">{c.name}</span>
                  <span className="sc-mono text-[color:var(--sc-volt)] text-xs">+{c.reward_credits}c</span>
                </div>
                {c.description && <div className="text-[11px] text-zinc-500 mb-1">{c.description}</div>}
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[color:var(--sc-pink)] to-[color:var(--sc-volt)]" style={{ width: `${pct}%` }} />
                </div>
                <div className="text-[10px] sc-mono text-zinc-500 mt-1">{my} / {c.target} · {c.action_type}{c.my_completed ? " · ✓ done" : ""}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Milestones */}
      <div className="sc-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-4 h-4 text-[color:var(--sc-orange)]" />
          <div className="sc-heading text-lg font-bold">Community Milestones</div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="community-milestones">
          {stats.achievements.map((a) => (
            <div key={a.key} className={`p-4 border rounded-sm text-center ${a.earned ? "border-[color:var(--sc-volt)]/40 bg-[color:var(--sc-volt)]/5" : "border-white/10 opacity-70"}`}>
              <div className="text-3xl">{a.icon}</div>
              <div className="sc-mono text-xs font-bold tracking-wider mt-2">{a.label}</div>
              <div className="h-1 bg-white/5 rounded-full overflow-hidden mt-2">
                <div className="h-full bg-[color:var(--sc-volt)]" style={{ width: `${a.progress}%` }} />
              </div>
              <div className="text-[10px] text-zinc-500 mt-1 sc-mono">{a.progress}%</div>
            </div>
          ))}
        </div>
      </div>

      {/* Members leaderboard */}
      <div className="sc-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <Gamepad2 className="w-4 h-4 text-[color:var(--sc-cyan)]" />
          <div className="sc-heading text-lg font-bold">Members Leaderboard</div>
          <div className="ml-auto flex gap-1 flex-wrap" data-testid="community-sort">
            {["xp", "tips_total", "watch_minutes", "games_won", "chat_count"].map((k) => (
              <button
                key={k}
                onClick={() => setSortBy(k)}
                data-testid={`community-sort-${k}`}
                className={`px-2 py-1 text-[10px] sc-mono tracking-widest uppercase rounded-sm ${sortBy === k ? "bg-white/10 text-white" : "text-zinc-500 hover:text-white"}`}
              >
                {k.replace("_", " ")}
              </button>
            ))}
          </div>
        </div>
        {leaderboard.length === 0 && <div className="text-xs text-zinc-500">No members yet — be the first!</div>}
        <div className="space-y-1">
          {leaderboard.map((m, i) => (
            <div key={m.username} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-b-0" data-testid={`community-member-${m.username}`}>
              <div className="w-6 text-center text-xs sc-mono text-zinc-500">#{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">
                  {m.username}
                  {m.is_founder && <span className="text-[9px] sc-mono px-1 py-0.5 rounded-sm border border-[#F59E0B]/60 text-[#F59E0B] ml-1">FOUNDER</span>}
                </div>
                <div className="text-[10px] text-zinc-500 sc-mono">Lv.{m.level} · {m.xp || 0} XP</div>
              </div>
              <div className="text-xs sc-mono text-zinc-400">
                {sortBy === "xp" && `${m.xp} XP`}
                {sortBy === "tips_total" && `${m.tips_total || 0}c tipped`}
                {sortBy === "watch_minutes" && `${m.watch_minutes || 0}m`}
                {sortBy === "games_won" && `${m.games_won || 0} wins`}
                {sortBy === "chat_count" && `${m.chat_count || 0} chats`}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatBox({ icon: Icon, label, value, tone, testid }) {
  return (
    <div className="p-4 border border-white/10 rounded-sm" data-testid={testid}>
      <div className="flex items-center gap-1.5 sc-label" style={{ color: tone }}>
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="sc-heading text-2xl font-black mt-1">{value}</div>
    </div>
  );
}
